
interface AdminCacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

interface AdminDashboardCache {
  stats: any;
  users: any[];
  verifications: any[];
  systemHealth: any;
  mediaFiles: any[];
  profilePhotos: any[];
}

export class AdminCacheService {
  private static instance: AdminCacheService;
  private cache = new Map<string, AdminCacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly STORAGE_TTL = 10 * 60 * 1000; // 10 minutes for storage data
  private readonly LONG_STORAGE_TTL = 30 * 60 * 1000; // 30 minutes for all files cache
  private readonly STATS_TTL = 2 * 60 * 1000; // 2 minutes for stats

  static getInstance(): AdminCacheService {
    if (!AdminCacheService.instance) {
      AdminCacheService.instance = new AdminCacheService();
    }
    return AdminCacheService.instance;
  }

  set<T>(key: string, data: T, customTTL?: number): void {
    const ttl = customTTL || this.DEFAULT_TTL;
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  invalidate(pattern: string): void {
    const keysToDelete: string[] = [];
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  clear(): void {
    this.cache.clear();
  }

  // Specific cache methods for admin data
  setDashboardStats(data: any): void {
    this.set('dashboard_stats', data, this.STATS_TTL);
  }

  getDashboardStats(): any | null {
    return this.get('dashboard_stats');
  }

  setUsers(data: any[]): void {
    this.set('admin_users', data, this.DEFAULT_TTL);
  }

  getUsers(): any[] | null {
    return this.get('admin_users');
  }

  setVerifications(data: any[]): void {
    this.set('admin_verifications', data, this.DEFAULT_TTL);
  }

  getVerifications(): any[] | null {
    return this.get('admin_verifications');
  }

  setStorageFiles(key: string, data: any, customTTL?: number): void {
    const ttl = customTTL || this.STORAGE_TTL;
    this.set(`storage_${key}`, data, ttl);
  }

  getStorageFiles(key: string): any | null {
    return this.get(`storage_${key}`);
  }

  setSystemHealth(data: any): void {
    this.set('system_health', data, this.STATS_TTL);
  }

  getSystemHealth(): any | null {
    return this.get('system_health');
  }

  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  private estimateMemoryUsage(): number {
    let totalSize = 0;
    for (const entry of this.cache.values()) {
      try {
        totalSize += JSON.stringify(entry.data).length * 2; // Rough estimate
      } catch {
        totalSize += 1024; // Default size if serialization fails
      }
    }
    return totalSize;
  }
}

export const adminCacheService = AdminCacheService.getInstance();
